import { useEffect, useState } from 'react';
import { Bot, User } from 'lucide-react';
import { cn } from '@/lib/cn';
import { isSystemAccount } from '@/constants/systemAccount';

interface AvatarProps {
  src?: string | null;
  alt?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  /** 작성자 user_id — 시세공간봇(SYSTEM_ACCOUNT) 이면 Bot 아이콘으로 전환 */
  userId?: string | null;
}

const SIZE_MAP = {
  xs: { box: 'h-6 w-6', icon: 'h-3.5 w-3.5' },
  sm: { box: 'h-8 w-8', icon: 'h-4 w-4' },
  md: { box: 'h-10 w-10', icon: 'h-5 w-5' },
  lg: { box: 'h-16 w-16', icon: 'h-8 w-8' },
};

/**
 * 내부 업로드 경로만 표시 허용. 외부 URL 은 User 아이콘 폴백.
 * — 소셜 로그인 이미지 직접 노출 금지 정책 강제
 */
function isAllowedAvatarSrc(src?: string | null): boolean {
  if (!src) return false;
  return src.startsWith('/uploads/');
}

export function Avatar({ src, alt = '', size = 'md', className, userId }: AvatarProps) {
  const { box, icon } = SIZE_MAP[size];
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setErrored(false);
  }, [src]);

  const isBot = isSystemAccount(userId);
  const showImage = !isBot && isAllowedAvatarSrc(src) && !errored;

  // 시세공간봇 — primary 배경 + Bot 아이콘 (화이트)
  if (isBot) {
    return (
      <div
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--color-primary)] text-white',
          box,
          className,
        )}
        aria-label="시세공간봇"
      >
        <Bot className={icon} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)]',
        box,
        className,
      )}
    >
      {showImage ? (
        <img
          key={src!}
          src={src!}
          alt={alt}
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
          referrerPolicy="no-referrer"
        />
      ) : (
        <User className={icon} />
      )}
    </div>
  );
}
